import { IShipmentPrice } from "@/types/apiResponse.types";
import { ICalculatorDetails } from "../types";
import { Country, TransportationMode } from "@/enums";

type Props = {
  values: ICalculatorDetails;
  shipmentPricesData: IShipmentPrice;
  isBuyForYou: boolean;
};

//  calculate transportation price
const calculateTransportationPrice = ({
  fromCountry,
  selectedDeliveryTypeId,
  deliveryPrice,
}: {
  fromCountry: number;
  selectedDeliveryTypeId: number;
  deliveryPrice: IShipmentPrice["deliveryPrice"];
}) => {
  return deliveryPrice.fromCountryId[fromCountry][
    selectedDeliveryTypeId === 1 ? "airPriceNational" : "seaPriceNational"
  ];
};

// calculate minimum weight
const calculateMinWeight = (
  weight: number,
  minWeightForCalculateKg: number,
) => {
  return Number(weight) < minWeightForCalculateKg
    ? minWeightForCalculateKg
    : Number(weight);
};

// calculate parcel duty
const calculateParcelDuty = (
  cost: number,
  customPriceLimitNational: number,
  parcelDutyPercent: number,
  currentRateAdjustmentValue: number,
) => {
  return cost > customPriceLimitNational
    ? (cost - customPriceLimitNational) *
        parcelDutyPercent *
        currentRateAdjustmentValue
    : 0;
};

// calculate sum of parcel sides
const calculateSumOfParcelSides = (
  length: number,
  width: number,
  height: number,
) => {
  return Number(length) + Number(width) + Number(height);
};

// calculate extra charge for oversized parcels
const calculateOversizedParcelExtra = (
  sumOfParcelSides: number,
  minParcelSidesCm: number,
  dimensions: ICalculatorDetails["details"],
) => {
  const { height, width, length } = dimensions;

  return sumOfParcelSides >= minParcelSidesCm
    ? (height * width * length) / 6000
    : 0;
};

const calculateAdditionalServicesPrice = (
  additionalServices: number[],
  shipmentPricesData: IShipmentPrice,
  cost: number,
  weight: number,
  minWeight: number,
  oversidedParcelExtra: number,
) => {
  const totalAdditionalServicesPrice = additionalServices.reduce(
    (acc, service) => {
      const shipmentServicestoArray = Object.values(
        shipmentPricesData?.shipmentServices,
      );
      const servicePrice =
        shipmentServicestoArray.find((item) => item.serviceId === service)
          ?.servicePriceNational || 0;

      switch (service) {
        case 1:
          return acc + servicePrice * 2;
        case 113:
          return acc + (cost * servicePrice) / 100;
        // case 211:
        //   return (
        //     acc +
        //     servicePrice * Math.max(weight, minWeight, oversidedParcelExtra)
        //   );
        default:
          return acc + servicePrice;
      }
    },
    0,
  );
  return parseFloat(totalAdditionalServicesPrice.toFixed(2));
};

// calculate all costs
export const calculateShipmentPriceUZ = ({
  values: { details, fromCountry, selectedDeliveryTypeId, additionalServices },
  shipmentPricesData,
  isBuyForYou,
}: Props) => {
  let transportationPrice = 0;
  let additionalServicesPrice = 0;
  let oversidedParcelExtra = 0;
  let minWeight = 0;
  let parcelDuty = 0;
  let totalCost = 0;
  let isBuyForYouCost = 0;

  const weightRoundedUp = Math.ceil(details.weight * 10) / 10;

  const isValid =
    details.weight &&
    details.cost &&
    details.length &&
    details.width &&
    details.height;

  if (isValid && shipmentPricesData) {
    transportationPrice = calculateTransportationPrice({
      fromCountry,
      selectedDeliveryTypeId,
      deliveryPrice: shipmentPricesData?.deliveryPrice,
    });

    minWeight = calculateMinWeight(
      weightRoundedUp,
      shipmentPricesData?.minWeightForCalculateKg,
    );

    parcelDuty = calculateParcelDuty(
      details.cost,
      shipmentPricesData?.customPriceLimitNational,
      shipmentPricesData?.parcelDutyPercent,
      shipmentPricesData?.currentRateAdjustmentValue,
    );

    const sumOfParcelSides = calculateSumOfParcelSides(
      details.length,
      details.width,
      details.height,
    );

    oversidedParcelExtra = parseFloat(
      calculateOversizedParcelExtra(
        sumOfParcelSides,
        shipmentPricesData?.minimumAmountOfParcelSidesCentimeters,
        details,
      ).toFixed(2),
    );

    if (additionalServices.length > 0) {
      additionalServicesPrice = calculateAdditionalServicesPrice(
        additionalServices,
        shipmentPricesData,
        details.cost,
        weightRoundedUp,
        minWeight,
        oversidedParcelExtra,
      );
    }

    if (isBuyForYou) {
      isBuyForYouCost = parseFloat(
        Math.max(
          shipmentPricesData.minCommissionForBuyForBuyRequestUsd,
          details.cost *
            (shipmentPricesData.minCommissionPercentForBuyForBuyRequestUsd /
              100),
        ).toFixed(2),
      );
    }

    let transportationCost: number;

    if (
      fromCountry === Country.USA &&
      selectedDeliveryTypeId === TransportationMode.AIR
    ) {
      transportationCost = transportationPrice * weightRoundedUp;
    } else if (
      fromCountry === Country.USA &&
      selectedDeliveryTypeId === TransportationMode.SEA
    ) {
      transportationCost = Math.max(
        transportationPrice * weightRoundedUp,
        shipmentPricesData?.minCostForWeightForDeliverySeaInUsd ?? 0,
      );
    } else {
      transportationCost =
        transportationPrice *
        Math.max(Number(weightRoundedUp), minWeight, oversidedParcelExtra);
    }

    totalCost =
      transportationCost +
      parcelDuty +
      additionalServicesPrice +
      isBuyForYouCost;
  }

  return {
    parcelDuty,
    transportationPrice,
    additionalServicesPrice,
    totalCost,
    oversidedParcelExtra,
    minWeight,
    isBuyForYouCost,
  };
};
